import { CircleCheckIcon, TriangleAlert } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import ExternalLinkIcon from 'icons/ExternalLink';
import { Maybe } from 'types/UtilityTypes';

import { LTLExternalQuote } from '../utils/classifyLTLQuoteResult';

type LTLExternalQuoteFeedbackProps = {
  externalQuote: LTLExternalQuote;
  onOpenQuote: (url: string) => void;
  // One line per message instead of the full banners, for dense lists.
  isCompact?: boolean;
};

// "Quote Saved in Tai" with the Open Quote link, or why the TMS could not save the quote.
export function LTLExternalQuoteFeedback({
  externalQuote,
  onOpenQuote,
  isCompact = false,
}: LTLExternalQuoteFeedbackProps) {
  const { url, id, source, label, status, errorMessage, assignment } =
    externalQuote;

  if (status === null) {
    return null;
  }

  const isExternalQuoteSuccess = status === 'success';
  const isExternalQuoteFailure = status === 'failure';
  const isAssignmentSuccess = assignment?.status === 'success';
  const isAssignmentFailure = assignment?.status === 'failure';
  const assignmentCarrierName = assignment?.carrierName;
  const assignmentFailureMessages = assignment?.messages?.filter(Boolean) ?? [];
  const assignmentFailureMessage =
    assignmentFailureMessages.length > 0
      ? assignmentFailureMessages.join(' ')
      : '3G could not assign the lowest carrier rate.';
  const displayQuoteId = id && id.length > 0 ? id : null;
  const displayProviderName = resolveQuoteSourceLabel(source, label);
  const successTitle = getSuccessTitle(
    displayProviderName,
    isAssignmentSuccess
  );
  const failureTitle = displayProviderName
    ? `Unable to save ${displayProviderName} quote`
    : 'Unable to save external quote';
  const failureDescription =
    errorMessage ??
    (displayProviderName
      ? `We couldn’t save this quote to ${displayProviderName}. You can still use the rates above.`
      : 'We couldn’t save this quote. You can still use the rates above.');

  if (isCompact) {
    return (
      <Flex
        direction='col'
        gap='xs'
        className='w-full'
        data-testid='ltl-external-quote-feedback'
      >
        {isExternalQuoteSuccess && url && (
          <Flex align='center' gap='xs' wrap='wrap' className='w-full'>
            <CircleCheckIcon className='h-3 w-3 shrink-0 text-success-600' />
            <Typography variant='body-xxs' className='text-success-700'>
              {successTitle}
              {isAssignmentSuccess && assignmentCarrierName
                ? ` (${assignmentCarrierName})`
                : ''}
            </Typography>
            <button
              type='button'
              className='text-xs'
              onClick={() => onOpenQuote(url)}
            >
              <span className='inline-flex items-center gap-1'>
                {formatQuoteCta(displayQuoteId)}
                <ExternalLinkIcon className='h-3 w-3' />
              </span>
            </button>
          </Flex>
        )}
        {isExternalQuoteSuccess && isAssignmentFailure && (
          <CompactWarning
            text={`Quote saved in 3G, but carrier assignment failed. ${assignmentFailureMessage}`}
          />
        )}
        {isExternalQuoteFailure && (
          <CompactWarning text={`${failureTitle}. ${failureDescription}`} />
        )}
      </Flex>
    );
  }

  if (isExternalQuoteSuccess && url) {
    const quoteCtaLabel = formatQuoteCta(displayQuoteId);

    return (
      <Flex direction='col' gap='sm' className='pt-2 w-full mx-0'>
        <div className='w-full rounded-lg border border-success-200 bg-success-50 px-4 py-3'>
          <div className='grid grid-cols-[auto_1fr] items-start gap-3'>
            <div className='mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-success-100'>
              <CircleCheckIcon className='h-4 w-4 text-success-600' />
            </div>
            <div>
              <Typography weight='semibold' className='text-success-700'>
                {successTitle}
              </Typography>
              {isAssignmentSuccess && assignmentCarrierName && (
                <Typography variant='body-xs' className='mt-1'>
                  Assigned carrier: {assignmentCarrierName}
                </Typography>
              )}
              <Typography variant='body-sm' className='mt-1'>
                <button
                  type='button'
                  className='text-link'
                  onClick={() => onOpenQuote(url)}
                >
                  <span className='inline-flex items-center gap-1'>
                    {quoteCtaLabel}
                    <ExternalLinkIcon className='h-3 w-3' />
                  </span>
                </button>
              </Typography>
            </div>
          </div>
        </div>
        {isAssignmentFailure && (
          <div className='w-full rounded-md border border-warning-500 bg-warning-100 px-3 py-3'>
            <Flex align='start' gap='sm'>
              <TriangleAlert className='h-4 w-4 text-warning-600 shrink-0 mt-0.5' />
              <Flex direction='col' gap='xs'>
                <Typography variant='body-sm' weight='medium'>
                  Quote saved in 3G, but carrier assignment failed
                </Typography>
                <Typography variant='body-xs'>
                  {assignmentFailureMessage}
                </Typography>
              </Flex>
            </Flex>
          </div>
        )}
      </Flex>
    );
  }

  if (isExternalQuoteFailure) {
    return (
      <div className='pt-2 w-full'>
        <div className='w-full rounded-md border border-warning-500 bg-warning-100 px-3 py-3'>
          <Flex align='start' gap='sm'>
            <TriangleAlert className='h-4 w-4 text-warning-600 shrink-0 mt-0.5' />
            <Flex direction='col' gap='xs'>
              <Typography variant='body-sm' weight='medium'>
                {failureTitle}
              </Typography>
              <Typography variant='body-xs'>{failureDescription}</Typography>
            </Flex>
          </Flex>
        </div>
      </div>
    );
  }

  return null;
}

function CompactWarning({ text }: { text: string }) {
  return (
    <Flex align='start' gap='xs' className='w-full'>
      <TriangleAlert className='mt-0.5 h-3 w-3 shrink-0 text-warning-600' />
      <Typography variant='body-xxs' className='text-warning-800'>
        {text}
      </Typography>
    </Flex>
  );
}

function resolveQuoteSourceLabel(
  source?: Maybe<string>,
  providedLabel?: Maybe<string>
): Maybe<string> {
  if (!source) {
    return providedLabel ?? null;
  }

  const normalizedSource = source.toLowerCase();
  if (normalizedSource === 'tai') {
    return 'Tai';
  }
  if (normalizedSource === 'threeg' || normalizedSource === '3g') {
    return '3G';
  }

  return providedLabel ?? null;
}

function getSuccessTitle(
  displayProviderName: Maybe<string>,
  isAssignmentSuccess: boolean
): string {
  const savedTitle = displayProviderName
    ? `Quote Saved in ${displayProviderName}`
    : 'Quote Saved';

  if (!isAssignmentSuccess) {
    return savedTitle;
  }

  return displayProviderName
    ? `Quote Saved in ${displayProviderName} and Carrier Assigned`
    : 'Quote Saved and Carrier Assigned';
}

function formatQuoteCta(explicitId: Maybe<string>): string {
  return explicitId ? `Open Quote ${explicitId}` : 'Open Quote';
}
